import React, { useRef } from 'react';
import { ActionIcon } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import clsx from 'clsx';
import { MdClose, MdEdit, MdSwapVert } from 'react-icons/md';
import Button from '@srm/ui/button';
import Tooltip from '@srm/ui/tooltip';
import {
    IMatrixCell,
    IMatrixField,
    IMatrixParticipant,
    IMatrixPosition,
    MatrixPriceMode,
} from '../WinnerSelection2.types';
import type { PositionCriterionHandler } from '../WinnerSelection2.types';
import {
    COMPLIANCE_LABEL,
    formatFieldValue,
    formatMatrixMessage,
    formatMoney,
    formatQuantity,
    metricNote,
    priceModeShort,
    resultLabel,
} from '../utils/matrixView';
import MatrixWarning from './MatrixWarning';
import PositionCriterion from './PositionCriterion';

const COMPLIANCE_CELL_LABEL = {
    ...COMPLIANCE_LABEL,
    compliant: 'Соответствует ТЗ',
    partial: 'Соответствует частично',
    noncompliant: 'Не соответствует ТЗ',
};

export type CellIntent = 'details' | 'choose' | 'compliance' | 'reset';

interface IProps {
    cell: IMatrixCell | undefined;
    position: IMatrixPosition;
    participant: IMatrixParticipant;
    headers: string;
    showComplianceComment?: boolean;
    priceMode: MatrixPriceMode;
    priceVisible: boolean;
    /** Показатель вместо цены за единицу; null — цена за единицу. */
    metric: IMatrixField | null;
    /** Название следующего показателя — подпись под числом переключает его во всей матрице. */
    nextMetricTitle: string | null;
    onNextMetric: () => void;
    busy: boolean;
    onPositionCriterion: PositionCriterionHandler;
    onIntent: (intent: CellIntent, cell: IMatrixCell, trigger: HTMLElement) => void;
}

const OfferCell: React.FC<IProps> = ({
    cell,
    position,
    participant,
    headers,
    showComplianceComment,
    priceMode,
    priceVisible,
    metric,
    nextMetricTitle,
    onNextMetric,
    busy,
    onPositionCriterion,
    onIntent,
}) => {
    const detailsButton = useRef<HTMLButtonElement>(null);
    const { ref: footerRef, height: footerHeight } = useElementSize<HTMLDivElement>();

    if (!cell) {
        return (
            <td className="ws2-cell ws2-cell--empty" headers={headers}>
                <div className="ws2-cell__absence">
                    <span className="ws2-cell__absence-mark" aria-hidden>
                        —
                    </span>
                    <span>Предложение не подано</span>
                </div>
            </td>
        );
    }

    const context = `${participant.name}, ${position.name}`;
    const priceValue = metric
        ? formatFieldValue(metric, cell.fields?.[metric.key])
        : priceVisible
          ? formatMoney(cell.price, cell.currency)
          : 'Цена скрыта';
    const priceNote = metric
        ? metricNote(metric, priceMode)
        : `за единицу · ${priceModeShort(priceMode)}${cell.priceIsRubFallback ? ' · в рублях' : ''}`;
    const assigned = cell.result === 'main' || cell.result === 'reserve';
    const unit = cell.unit ?? position.unit ?? '';
    const open = (intent: CellIntent) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onIntent(intent, cell, event.currentTarget);
    };

    const detailsButtonProps = {
        ref: detailsButton,
        type: 'button' as const,
        'aria-label': `Детали предложения: ${context}`,
        onClick: open('details'),
    };

    const openCellDetails = (event: React.MouseEvent<HTMLTableCellElement>) => {
        // Nested actions (including disabled controls) keep their own behavior.
        if (
            (event.target as Element).closest(
                'button, a, input, select, textarea, [role="button"], .ws2-cell__tech-root',
            )
        )
            return;
        onIntent('details', cell, detailsButton.current ?? event.currentTarget);
    };

    const renderDecision = () => {
        if (assigned || cell.result === 'other') {
            const canReset = cell.canEditQuantity && !cell.lotOffer && cell.result !== 'other';
            return (
                <div
                    className={clsx(
                        'ws2-cell__decision',
                        cell.result === 'reserve' && 'ws2-cell__decision--reserve',
                        cell.result === 'other' && 'ws2-cell__decision--other',
                    )}
                    key={cell.result}
                >
                    <span className="ws2-cell__decision-text">
                        {resultLabel(cell.result, cell.resultText)}
                        {assigned && (
                            <>
                                {' '}
                                · {formatQuantity(cell.effectiveQuantity)} {unit}
                            </>
                        )}
                        {cell.lotOffer && <small>По всему лоту</small>}
                    </span>
                    {canReset && (
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            aria-label={`Сбросить выбор: ${context}`}
                            title="Сбросить выбор и количество"
                            disabled={busy}
                            onClick={open('reset')}
                        >
                            <MdClose size="16px" aria-hidden />
                        </ActionIcon>
                    )}
                </div>
            );
        }

        if (cell.canDecide) {
            return (
                <Button
                    variant="outline-neutral"
                    className="ws2-cell__choose"
                    size="auto"
                    disabled={busy}
                    onClick={open('choose')}
                    aria-label={`Оценить предложение: ${context}`}
                >
                    Оценить предложение
                </Button>
            );
        }

        return cell.decisionReason && cell.canEditQuantity ? (
            <span className="ws2-cell__hint" title={formatMatrixMessage(cell.decisionReason)}>
                {cell.lotOffer
                    ? 'Выбор по всему лоту'
                    : cell.compliance === 'unanswered'
                      ? 'Нужен допуск по ТЗ'
                      : formatMatrixMessage(cell.decisionReason)}
            </span>
        ) : null;
    };

    const hasComment = !!cell.complianceComment?.trim();
    const complianceText = (
        <span>
            {cell.complianceAnswer ? COMPLIANCE_CELL_LABEL[cell.compliance] : 'Нет ответа'}
            {hasComment && !showComplianceComment ? ' · есть комментарий' : ''}
        </span>
    );
    const complianceContent = (
        <>
            <span className="ws2-cell__tech-line">
                <span className="ws2-cell__tech-label">ТЗ</span>
                <span className={clsx('ws2-cell__tech-answer', `ws2-cell__tech-answer--${cell.compliance}`)}>
                    {complianceText}
                </span>
                {cell.canEditCompliance && <MdEdit className="ws2-cell__tech-icon" size="16px" aria-hidden />}
            </span>
            {showComplianceComment && hasComment && (
                <span className="ws2-cell__comment-inline">{cell.complianceComment}</span>
            )}
        </>
    );
    const complianceFooter = cell.canEditCompliance ? (
        <button
            type="button"
            className="ws2-cell__tech ws2-cell__tech--editable"
            aria-label={`Изменить ответ по ТЗ: ${context}`}
            disabled={busy}
            onClick={open('compliance')}
        >
            {complianceContent}
        </button>
    ) : (
        <div className="ws2-cell__tech">{complianceContent}</div>
    );

    return (
        <td
            className={clsx(
                'ws2-cell',
                cell.result === 'main' && 'ws2-cell--main',
                cell.result === 'reserve' && 'ws2-cell--reserve',
            )}
            headers={headers}
            data-testid="ws2-cell"
            style={{ '--ws2-footer-height': `${footerHeight || 44}px` } as React.CSSProperties}
            onClick={openCellDetails}
        >
            <div className="ws2-cell__body">
                <div className="ws2-cell__top">
                    {nextMetricTitle == null ? (
                        <button {...detailsButtonProps} className="ws2-cell__price">
                            <span className="ws2-cell__price-value">{priceValue}</span>
                            <span className="ws2-cell__price-note">{priceNote}</span>
                        </button>
                    ) : (
                        <div className="ws2-cell__price ws2-cell__price--metrics">
                            <button {...detailsButtonProps} className="ws2-cell__price-value">
                                {priceValue}
                            </button>
                            <button
                                type="button"
                                className="ws2-cell__price-note ws2-cell__metric-switch"
                                title={`Показать во всех предложениях: ${nextMetricTitle}`}
                                aria-label={`${priceNote}. Показать во всех предложениях: ${nextMetricTitle}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onNextMetric();
                                }}
                            >
                                {priceNote}
                                <MdSwapVert size="14px" aria-hidden />
                            </button>
                        </div>
                    )}
                    {cell.rank != null && (
                        <span
                            className="ws2-cell__rank"
                            data-rank={cell.rank}
                            title="Ранг помогает сравнить цены и не ограничивает выбор"
                        >
                            Ранг<b>{cell.rank}</b>
                        </span>
                    )}
                </div>

                <div className="ws2-cell__quantity">
                    <span>Выбрано</span>
                    <Button
                        variant="text"
                        className="ws2-cell__link"
                        size="sm"
                        onClick={open('details')}
                        aria-label={`Выбранное количество: ${context}`}
                    >
                        {formatQuantity(cell.effectiveQuantity ?? 0)} {unit}
                    </Button>
                </div>

                <div className="ws2-cell__selection">{renderDecision()}</div>

                {cell.warnings.map((warning) => (
                    <MatrixWarning key={warning}>{formatMatrixMessage(warning)}</MatrixWarning>
                ))}

                <div className="ws2-cell__tech-root" ref={footerRef}>
                    <span className="ws2-cell__details-hint" aria-hidden>
                        Подробнее
                    </span>
                    {hasComment && !showComplianceComment ? (
                        <Tooltip
                            followCursor
                            wrapTarget={false}
                            interactive={false}
                            background="black"
                            position="right"
                            offsetValue={{ mainAxis: 10 }}
                            maxWidth={320}
                            label={cell.complianceComment}
                        >
                            {complianceFooter}
                        </Tooltip>
                    ) : (
                        complianceFooter
                    )}
                    {cell.criteria?.map((criterion) => (
                        <PositionCriterion
                            key={criterion.item.critId}
                            criterion={criterion}
                            context={context}
                            busy={busy}
                            onOpen={(trigger) => onPositionCriterion(cell, criterion, trigger)}
                        />
                    ))}
                </div>
            </div>
        </td>
    );
};

export default React.memo(OfferCell);
